/**
 * voice-v8 — Werkzeugkarten: Gemeinsame Helfer der Werkzeugkarten: Ergebnis auspacken, kürzen, Datum, Name, Bildproxy, Rückfall auf die Anzeige.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { STAND_NICHT_AUSGEFUEHRT, STAND_WARTET } from "../karten-text.js";
import type { ToolCardMessage } from "../webrtc.js";
import { anzeigeVon, baueAnzeigeKarte, leiseKarte } from "../anzeige-karte.js";
import { chips, leerHinweis } from "../karte.js";
import { werkzeugName } from "../werkzeug-namen.js";

/**
 * Blender über Vespera Desktop. (29.09.2026, Ticket 2806) Nie den Rohtext:
 * `blender_bild` trägt ein base64-Bild von mehreren hundert KB.
 */
/**
 * Das Werkzeug hat NICHTS getan und wartet auf Martins Ja (Ja-Tor,
 * server/sitzungs-ja.ts). (15.10.2026) Vorher stand hier der Hinweis an das
 * Modell („NICHTS in Blender ausgeführt. Frag den Nutzer in einem Satz: …“)
 * als Fehlerkasten. Jetzt: eine Pille und ein Satz an Martin — kein Fehler,
 * denn die Frage selbst stellt Vespera im Text oder in der Rückfrage-Karte.
 */
export function freigabeStand(el: HTMLElement): HTMLElement {
  chips(el, [STAND_WARTET]);
  leerHinweis(el, STAND_NICHT_AUSGEFUEHRT);
  return el;
}

// 2026-07-15 (Martin): externe Bild-Suche-Treffer ueber den Server-Proxy
// (/api/img) laden — gleiche Herkunft wie die App, damit Hotlink-Schutz,
// Referrer-Sperren und WKWebView-External-Image-Probleme wegfallen. Eigene
// (gleicher Origin/relativ) Bilder + data:-URLs bleiben direkt.
export function proxiedImg(url: string, ref?: string): string {
  if (!url) return "";
  if (url.startsWith("data:")) return url;
  try {
    const u = new URL(url, location.href);
    if (u.origin === location.origin) return url;
    if (u.protocol !== "http:" && u.protocol !== "https:") return url;
  } catch {
    return url;
  }
  let q = `/api/img?u=${encodeURIComponent(url)}`;
  if (ref) q += `&ref=${encodeURIComponent(ref)}`;
  return q;
}

/**
 * Klartext-Name fuer den Karten-Kopf. (2026-07-04: nie interne Namen wie
 * "route_directions" zeigen.) 06.09.2026: Die Tabelle hier kannte ~40
 * Werkzeuge; alle anderen hiessen im Chat englisch in Title-Case ("Ticket
 * List Open" — Martins Bild). Jetzt liegt EIN Verzeichnis fuer alle 231
 * registrierten Werkzeuge in werkzeug-namen.ts, mit Test auf Vollstaendigkeit.
 */
export function toolLabel(tool: string): string {
  return werkzeugName(tool);
}

/** Nur der Name der Seite — niemand liest eine volle Adresse mit Parametern. */
export function hostVon(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

/**
 * Kein eigener Bau für dieses Werkzeug. (15.10.2026, Plan „Karten zu 100 % einheitlich“)
 *
 * Bis heute stand hier `buildGenericCard`: das rohe Ergebnis als Feldliste, Liste, Textblock — 157 von 301 Werkzeugen
 * landeten darin („Datum / Text {…}“, „Rohdaten anzeigen“). Jetzt rendert die Karte NUR die Anzeige des Servers
 * (server/werkzeug-anzeige.ts, lib/anzeige-karte.ts); ohne Anzeige (eine alte gespeicherte Karte) ist es eine leise
 * Zeile, die app.ts in die Schritte des Zuges hängt. Das Rohergebnis sieht nur das Modell.
 */
export function ohneEigeneKarte(msg: ToolCardMessage): HTMLElement {
  const a = anzeigeVon(msg);
  return a ? baueAnzeigeKarte(a) : leiseKarte(msg.tool, msg.isError);
}

// --- Helpers for the new pretty cards ----------------------------------

export function unwrapResult(result: unknown): Record<string, unknown> {
  /*
   * 28.09.2026 (Ticket 2727): ein doppelt verpacktes Ergebnis — JSON-Text in
   * Anführungszeichen — einmal auspacken. browser_tab_context lieferte so bis
   * pre.3426; im Verlauf stehen 16 solche Karten, die sonst beim Neuladen
   * wieder roh ({\"ok\":true,…}) erschienen.
   */
  if (typeof result === "string" && result.trim().startsWith('"')) {
    try {
      const innen: unknown = JSON.parse(result.trim());
      if (typeof innen === "string") return unwrapResult(innen);
    } catch {
      /* kein JSON-Text — unten als Klartext */
    }
  }
  if (typeof result === "string") {
    const trimmed = result.trim();
    // JSON?
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === "object" && parsed !== null) {
          return parsed as Record<string, unknown>;
        }
      } catch {
        /* fall through */
      }
    }
    // Pre.267: Plain-Text-Returns sind KEIN Fehler. Viele v7-Tools
    // (faktura, newsletter, trello) returnen formatierten Text statt
    // strukturiertes JSON. Als output behandeln, nicht als error.
    return { ok: true, output: result };
  }
  return (result ?? {}) as Record<string, unknown>;
}

export function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dateOnly = !iso.includes("T");
  return d.toLocaleString("de-AT", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    ...(dateOnly ? {} : { hour: "2-digit", minute: "2-digit" }),
  });
}

export function fmtDateShort(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-AT", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
}

export function trimUrl(url: string, max: number = 60): string {
  if (!url) return "";
  return url.length > max ? url.slice(0, max) + "…" : url;
}

/**
 * Kürzen, ohne mitten im Wort abzuschneiden.
 *
 * ⚠️ 2026-08-30 (Martin ueber die Trefferkarte: *„das kann auch viel schoener
 * formatiert werden als so"*): Vorher schnitt das hart nach N Zeichen — in
 * seinem Bild endete die Suchzeile mitten in einem Wort. Ein abgehacktes Wort
 * liest sich wie ein Fehler, auch wenn nur gekuerzt wurde.
 *
 * Die Grenze wird nur nach hinten gesucht, nie nach vorn: lieber ein paar
 * Zeichen kuerzer als laenger als erlaubt.
 */
export function trimText(s: string, max: number): string {
  if (!s) return "";
  if (s.length <= max) return s;
  const roh = s.slice(0, max);
  const luecke = roh.lastIndexOf(" ");
  // Nur wenn dabei nicht mehr als ein Viertel verloren geht — sonst waere ein
  // langes Wort ohne Leerzeichen (URL, Kennung) fast ganz weg.
  const kurz = luecke > max * 0.75 ? roh.slice(0, luecke) : roh;
  return kurz.replace(/[\s,;:.–-]+$/, "") + "…";
}
